import { useNavigate } from "react-router-dom";
import { Zap, Users, Clock, Lock, Sparkles, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

/** Format a timestamp/ISO string into "Nh left" / "Nd left" / "closed". */
export function formatTimeLeft(iso) {
  if (!iso) return "—";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "closed";
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m left`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h left`;
  const days = Math.floor(hours / 24);
  return `${days}d left`;
}

/**
 * Derive the correct status pill for an Available task card from live signals:
 * task.status + task.deadline + entered_count + entry_cap.
 * Fixes the bug where "closed" was shown by default even for live rounds.
 */
function deriveAvailableStatus(t) {
  const now = Date.now();
  const cap = t.entry_cap || t.applicant_cap || 0;
  const entered = t.entered_count || 0;
  const deadlineMs = t.deadline ? new Date(t.deadline).getTime() : null;
  const deadlinePast = deadlineMs != null && deadlineMs <= now;
  const isFinalized = t.status === "finalized";
  const isRanked = t.status === "ranked";
  const isRanking = t.status === "locked" || t.status === "awaiting_ranking";
  const isFull = cap > 0 && entered >= cap;

  if (isFinalized) return { label: "Finalized", Icon: CheckCircle2, tone: "text-emerald-700" };
  if (isRanked)    return { label: "Ranked",    Icon: CheckCircle2, tone: "text-emerald-700" };
  if (isRanking)   return { label: "Ranking",   Icon: Sparkles,     tone: "text-amber-700" };
  if (deadlinePast) return { label: "Closed",   Icon: Lock,         tone: "text-neutral-500" };
  if (isFull)       return { label: "Full",     Icon: Users,        tone: "text-neutral-600" };
  return {
    label: `Open · ${formatTimeLeft(t.deadline)}`,
    Icon: Clock,
    tone: "text-amber-700",
  };
}

const CTA_BY_STATUS = {
  entered: { kind: "button", label: "Start task" },
  selected: { kind: "button", label: "Start task" }, // legacy
  submitted: { kind: "badge", label: "Awaiting AI ranking", tone: "bg-neutral-200 text-neutral-700" },
  completed: { kind: "badge", label: "Awaiting AI ranking", tone: "bg-neutral-200 text-neutral-700" },
  graded: { kind: "badge", label: "Ranked", tone: "bg-amber-100 text-amber-800" },
  lost: { kind: "badge", label: "No win this round", tone: "bg-neutral-200 text-neutral-500" },
  rejected: { kind: "badge", label: "Not selected", tone: "bg-neutral-200 text-neutral-500" },
  won: { kind: "badge", label: "Completed", tone: "bg-emerald-100 text-emerald-800" },
  paid: { kind: "badge", label: "Payout received", tone: "bg-emerald-100 text-emerald-800" },
};

// Permanent border + ring tint per entry status (matches Available's warm accent).
const ENTRY_RING_BY_STATUS = {
  submitted: "border-neutral-300 ring-1 ring-neutral-200/70 hover:border-neutral-400 hover:ring-neutral-300",
  completed: "border-neutral-300 ring-1 ring-neutral-200/70 hover:border-neutral-400 hover:ring-neutral-300",
  graded: "border-neutral-300 ring-1 ring-neutral-200/70 hover:border-neutral-400 hover:ring-neutral-300",
  entered: "border-neutral-300 ring-1 ring-neutral-200/70 hover:border-neutral-400 hover:ring-neutral-300",
  selected: "border-neutral-300 ring-1 ring-neutral-200/70 hover:border-neutral-400 hover:ring-neutral-300",
  won: "border-emerald-300 ring-1 ring-emerald-200/70 hover:border-emerald-400 hover:ring-emerald-300",
  paid: "border-emerald-300 ring-1 ring-emerald-200/70 hover:border-emerald-400 hover:ring-emerald-300",
  lost: "border-rose-200",
  rejected: "border-rose-200",
};

/**
 * A unified card used across the pipeline board (Available column) and
 * as an entry card (Entered/Selected/Completed/Paid columns).
 *
 * Props:
 *   variant: "available" | "entry"
 *   task:  full task object (for available)
 *   entry: task_entry object (for pipeline columns)
 *   onEnter, onOpen: callbacks
 */
export default function TaskCard({ variant, task, entry, onEnter, onOpen }) {
  const navigate = useNavigate();

  if (variant === "available") {
    const t = task;
    const status = deriveAvailableStatus(t);
    const { Icon: StatusIcon } = status;
    return (
      <div
        className="group flex flex-col gap-3 rounded-xl border border-amber-300 bg-white p-4 shadow-sm ring-1 ring-amber-200/70 transition hover:border-amber-400 hover:shadow-md hover:ring-amber-300"
        data-testid={`task-card-${t.task_id}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <button
              onClick={() => (onOpen ? onOpen(t) : navigate(`/app/tasks/${t.task_id}`))}
              className="line-clamp-2 text-left text-[15px] font-semibold leading-snug text-neutral-900 hover:text-amber-800"
              data-testid={`task-title-${t.task_id}`}
            >
              {t.title}
            </button>
            {t.has_entered && (
              <span
                className="mt-1 inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800 ring-1 ring-inset ring-amber-200"
                data-testid={`entered-chip-${t.task_id}`}
              >
                Entered
              </span>
            )}
          </div>
          <LanguagePill language={t.language} />
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-500">
          <span className="inline-flex items-center gap-1">
            <Zap className="h-3.5 w-3.5" />
            {t.entry_cost_points} pts entry
          </span>
          <span className="inline-flex items-center gap-1">
            <Users className="h-3.5 w-3.5" />
            {t.entered_count} entered
          </span>
        </div>

        <div
          className={`flex items-center gap-1 text-xs font-medium ${status.tone}`}
          data-testid={`task-status-${t.task_id}`}
        >
          <StatusIcon className="h-3.5 w-3.5" />
          <span>{status.label}</span>
        </div>

        {t.description && (
          <p
            className="mt-1 line-clamp-2 text-xs italic text-neutral-500"
            data-testid={`task-description-${t.task_id}`}
          >
            {t.description}
          </p>
        )}

        <div className="mt-1 flex items-center justify-between border-t border-neutral-100 pt-3">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wider text-neutral-400">
              Task reward
            </p>
            <p className="text-sm font-bold text-emerald-700">
              ₦{(((t.winner_reward_points ?? t.reward_points) || 0) * 10).toLocaleString()}
              {t.winners_count ? (
                <span className="ml-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                  {t.winners_count} winners
                </span>
              ) : null}
            </p>
          </div>
          {t.has_entered ? (
            <Button
              size="sm"
              disabled
              className="cursor-default rounded-lg border border-amber-300 bg-white text-amber-700 opacity-90 hover:bg-white disabled:opacity-90"
              data-testid={`entered-btn-${t.task_id}`}
            >
              Entered
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => (onEnter ? onEnter(t) : navigate(`/app/tasks/${t.task_id}`))}
              className="rounded-lg bg-amber-700 hover:bg-amber-800"
              data-testid={`enter-btn-${t.task_id}`}
            >
              Enter round
            </Button>
          )}
        </div>
      </div>
    );
  }

  // ------- entry variant ---------
  const e = entry;
  const cta = CTA_BY_STATUS[e.status] || CTA_BY_STATUS.entered;
  const paidAmount = e.payout_ngn
    ? `+₦${Number(e.payout_ngn).toLocaleString("en-NG")}`
    : e.payout_points
      ? `+${e.payout_points} pts`
      : null;

  return (
    <div
      className={`flex flex-col gap-3 rounded-xl border bg-white p-4 shadow-sm transition hover:shadow-md ${
        ENTRY_RING_BY_STATUS[e.status] || "border-neutral-200"
      }`}
      data-testid={`entry-card-${e.entry_id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="line-clamp-2 flex-1 text-[15px] font-semibold leading-snug text-neutral-900">
          {e.task_title}
        </p>
        <LanguagePill language={e.task_language} />
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-500">
        <span className="inline-flex items-center gap-1">
          <Zap className="h-3.5 w-3.5" />
          {e.entry_cost_points} pts entry
        </span>
        {e.ai_score != null && (
          <span className={`font-semibold tabular-nums ${
            e.ai_score === 100 ? "text-emerald-700" :
            e.ai_score >= 80 ? "text-amber-700" : "text-neutral-500"
          }`}>
            AI: {e.ai_score}/100{e.final_rank ? ` · #${e.final_rank}` : ""}
          </span>
        )}
      </div>

      <div className="mt-1 flex items-center justify-between border-t border-neutral-100 pt-3">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-wider text-neutral-400">
            Task reward
          </p>
          <p className="text-sm font-bold text-emerald-700">
            {(e.status === "won" || e.status === "paid")
              ? paidAmount
              : `₦${(((e.winner_reward_points ?? e.reward_points) || 0) * 10).toLocaleString()}`}
          </p>
        </div>
        {cta.kind === "button" ? (
          <Button
            size="sm"
            onClick={() => navigate(`/app/tasks/${e.task_id}/workspace`)}
            className="rounded-lg bg-amber-700 hover:bg-amber-800"
            data-testid={`start-btn-${e.entry_id}`}
          >
            {cta.label}
          </Button>
        ) : (
          <span
            className={`rounded-full px-3 py-1 text-xs font-semibold ${cta.tone}`}
          >
            {cta.label}
          </span>
        )}
      </div>
    </div>
  );
}

const LANG_COLORS = {
  Yoruba: "bg-amber-100 text-amber-800",
  Igbo: "bg-amber-100 text-amber-900",
  Hausa: "bg-amber-100 text-amber-800",
  Pidgin: "bg-amber-100 text-amber-900",
  English: "bg-amber-100 text-amber-900",
};

export function LanguagePill({ language }) {
  const cls = LANG_COLORS[language] || "bg-amber-100 text-amber-800";
  return (
    <span
      className={`shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${cls}`}
    >
      {language}
    </span>
  );
}
